import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useSeo } from "../lib/useSeo";
import { ArrowLeft, ArrowRight, Lock, Loader2, Shield, Scale, Gift, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

const TITLES = ["Mr", "Mrs", "Ms", "Miss", "Mx", "Dr"];
const REFERRAL_SOURCES = [
  { value: "google", label: "Google search" },
  { value: "google_ads", label: "Google ad" },
  { value: "facebook", label: "Facebook" },
  { value: "instagram", label: "Instagram" },
  { value: "referral", label: "Referral from a friend" },
  { value: "accountant", label: "My accountant" },
  { value: "trade_assoc", label: "Trade association" },
  { value: "newsletter", label: "Your newsletter" },
  { value: "other", label: "Other" },
];

const PLAN_PRICE = {
  monthly: { hero: "£15", suffix: "/month, VAT included", today: "£15.00", subtitle: "Then £15 every month. Cancel anytime.",
    breakdown: [
      { label: "Monthly subscription (inc VAT)", value: "£15.00" },
      { label: "Total today", value: "£15.00", bold: true },
    ]
  },
  annual: { hero: "£155.40", suffix: "/year, VAT included", today: "£155.40", subtitle: "One annual payment. Save £24.60 vs monthly.",
    breakdown: [
      { label: "Annual subscription", value: "£129.50" },
      { label: "VAT (20%)", value: "£25.90" },
      { label: "Total today", value: "£155.40", bold: true },
    ]
  },
};
const PLAN_LABEL = { monthly: "Monthly Protection", annual: "Annual Protection" };

const CURRENT_YEAR = new Date().getFullYear();
const LAUNCH_YEARS = Array.from({ length: 30 }, (_, i) => CURRENT_YEAR - i);

export default function CheckoutDetails() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const initialPlan = params.get("plan") === "monthly" ? "monthly" : "annual";
  const [planId, setPlanId] = useState(initialPlan);

  const [busy, setBusy] = useState(false);

  useSeo({ title: "Checkout - Your details", noindex: true });

  // Referral handling - capture `?ref=CODE` from the URL, validate via the public
  // /billing/check-referral endpoint, and show the customer whether they qualify
  // for the "first period free" reward before they hit Pay.
  const initialRef = (params.get("ref") || "").trim().toUpperCase();
  const [referralCode, setReferralCode] = useState(initialRef);
  const [referralStatus, setReferralStatus] = useState("idle"); // idle | checking | valid | invalid
  const [referralReward, setReferralReward] = useState("");

  useEffect(() => {
    const code = referralCode.trim().toUpperCase();
    let cancelled = false;
    const run = async () => {
      if (!code) { setReferralStatus("idle"); setReferralReward(""); return; }
      if (code.length < 4) { setReferralStatus("idle"); return; }
      setReferralStatus("checking");
      try {
        const { data } = await api.get(`/billing/check-referral/${encodeURIComponent(code)}`);
        if (cancelled) return;
        if (data?.valid) {
          setReferralStatus("valid");
          setReferralReward(data?.reward_description || "First period free with this referral code.");
        } else {
          setReferralStatus("invalid");
          setReferralReward("");
        }
      } catch {
        if (!cancelled) setReferralStatus("invalid");
      }
    };
    run();
    return () => { cancelled = true; };
  }, [referralCode]);

  const [form, setForm] = useState({
    title: "Mr",
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    house_number: "",
    street: "",
    city: "",
    county: "",
    postcode: params.get("postcode") || "",
    business_name: params.get("business_name") || "",
    nature_of_business: params.get("industry_label") || "",  // free text now
    business_launch: "",
    referral_source: "",
    notes: "",
  });

  // Compulsory T&C consent - user must tick this before the submit handler
  // will hit /checkout/session and hand off to Stripe. Kept in local state so
  // it resets naturally on re-mount.
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsError, setTermsError] = useState(false);

  // Track this as a `checkout_started` funnel event once on mount so admin can
  // see how many people reach this step versus complete it. Best-effort -
  // silently swallows errors so a network blip never disrupts the user flow.
  useEffect(() => {
    api.post("/funnel/event", {
      stage: "checkout_started",
      business_name: params.get("business_name") || null,
      industry: params.get("industry_label") || null,
      plan_id: initialPlan,
    }).catch(() => {});
    // Run once on first mount. We intentionally omit dependencies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const hasSearched = Boolean(form.business_name);
  const plan = PLAN_PRICE[planId];

  const referralActive = planId === "monthly" && referralStatus === "valid";

  // ---------------------------------------------------------------------------
  // GA4 `checkout_abandoned` - fires ONCE on `visibilitychange` (tab close,
  // switch, or backgrounding) when the user has not completed the submit
  // handler. `transport_type: 'beacon'` piggybacks on navigator.sendBeacon so
  // the event survives the tab actually closing. We use refs kept fresh on
  // every render so the effect can attach the listener once and still read
  // current form state - cheaper than re-attaching on every keystroke.
  // ---------------------------------------------------------------------------
  const abandonFiredRef = useRef(false);
  const submittedRef = useRef(false);
  const snapshotRef = useRef({ planId: initialPlan, form: {}, referralActive: false });
  snapshotRef.current = { planId, form, referralActive };

  useEffect(() => {
    const REQUIRED_KEYS = [
      "first_name", "last_name", "email", "phone",
      "house_number", "street", "city", "postcode",
      "business_name", "nature_of_business", "business_launch", "referral_source",
    ];
    const onVisibility = () => {
      if (document.visibilityState !== "hidden") return;
      if (abandonFiredRef.current || submittedRef.current) return;
      const s = snapshotRef.current;
      const filled = REQUIRED_KEYS.reduce(
        (n, k) => n + ((s.form?.[k] || "").toString().trim() ? 1 : 0),
        0,
      );
      // Don't log a "bounce" as an abandonment - only fire if the user
      // has meaningfully engaged with at least one field. Preserves the
      // event's signal quality in GA4.
      if (filled < 1) return;
      abandonFiredRef.current = true;
      if (typeof window !== "undefined" && typeof window.gtag === "function") {
        const planSuffix = s.planId === "monthly" ? "monthly" : "annually";
        window.gtag("event", `checkout_abandoned_${planSuffix}`, {
          plan_type: s.planId === "monthly" ? "buy now - monthly" : "buy now - annually",
          has_business_name: Boolean(s.form?.business_name),
          fields_filled_count: filled,
          referral_active: Boolean(s.referralActive),
          transport_type: "beacon",
        });
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.business_name) {
      toast.error("Please run a free name check first so we know which name to protect.");
      nav("/check");
      return;
    }
    // Compulsory T&C tick - surface as inline error + toast so screen-readers
    // and visual users both get feedback. Blocks the Stripe redirect entirely.
    if (!termsAccepted) {
      setTermsError(true);
      toast.error("Please confirm you have read and agree to the Terms and Conditions.");
      return;
    }
    setBusy(true);
    // Flip BEFORE the GA4 fire so a rapid tab-switch during the redirect
    // window can't backfire an "abandoned" event on a user we just converted.
    submittedRef.current = true;

    // GA4 `checkout_started_monthly` / `checkout_started_annually` - split by
    // plan so each variant shows as its own event in GA4 reports without
    // needing a Custom Dimension. `plan_type` is retained on both events so
    // future dashboards can still slice by the verbatim button copy.
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      const planSuffix = planId === "monthly" ? "monthly" : "annually";
      window.gtag("event", `checkout_started_${planSuffix}`, {
        plan_type: planId === "monthly" ? "buy now - monthly" : "buy now - annually",
        referral_active: referralActive,
      });
    }

    try {
      const { data } = await api.post("/checkout/session", {
        plan_id: planId,
        origin_url: window.location.origin,
        ...form,
        postcode: form.postcode || null,
        referral_code: referralActive ? referralCode.trim().toUpperCase() : null,
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("Could not start checkout. Please try again.");
        submittedRef.current = false; // failed → let abandonment tracking resume
        setBusy(false);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not start checkout. Please try again.");
      submittedRef.current = false; // failed → let abandonment tracking resume
      setBusy(false);
    }
  };

  const labelCls = "block text-xs font-display font-semibold tracking-wide uppercase text-[#447694] mb-1.5";

  return (
    <main className="py-12 md:py-16 lg:py-20 xl:py-24" data-testid="checkout-details-page">
      <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24">
        <Link to="/pricing" className="inline-flex items-center gap-1 text-sm text-[#447694] hover:text-[#04273d]" data-testid="checkout-back-link">
          <ArrowLeft size={14} /> Back to pricing
        </Link>

        <div className="mt-4 text-center max-w-2xl mx-auto">
          <span className="bnp-chip bnp-chip-teal">Step 2 of 2</span>
          <h1 className="font-display text-[clamp(1.75rem,3.5vw,3rem)] text-[#04273d] mt-3 font-bold tracking-tight">
            Your details
          </h1>
          <p className="text-[#447694] mt-2">
            One form, then secure payment via Stripe. Takes about 90 seconds.
          </p>
        </div>

        {/* Plan toggle */}
        <div className="mt-7 flex justify-center" data-testid="checkout-plan-toggle">
          <div className="inline-flex bg-white border border-[#9ac7f7]/40 rounded-full p-1.5 gap-1">
            <button
              type="button"
              onClick={() => setPlanId("monthly")}
              className={`px-5 py-2 rounded-full text-sm font-display font-semibold transition-colors ${planId === "monthly" ? "bg-[#04273d] text-white" : "text-[#04273d] hover:bg-[#9ac7f7]/20"}`}
              data-testid="checkout-plan-monthly"
            >
              Monthly · £15/mo
            </button>
            <button
              type="button"
              onClick={() => setPlanId("annual")}
              className={`px-5 py-2 rounded-full text-sm font-display font-semibold transition-colors ${planId === "annual" ? "bg-[#04273d] text-white" : "text-[#04273d] hover:bg-[#9ac7f7]/20"}`}
              data-testid="checkout-plan-annual"
            >
              Annual · £155.40 <span className="text-[#71edc9] text-[10px] ml-1">(save £24.60)</span>
            </button>
          </div>
        </div>

        <div className="mt-7 grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            {!hasSearched && (
              <div className="mb-6 bnp-card p-5 bg-amber-50 border-amber-200" data-testid="checkout-needs-search-warning">
                <p className="font-display font-semibold text-[#04273d]">Run a free name check first</p>
                <p className="text-sm text-[#447694] mt-1">We need to know which business name you&rsquo;re protecting. Use the free checker first, then come back here.</p>
                <Link to="/check" className="btn-primary text-sm mt-3 inline-flex">Run a free check <ArrowRight size={14} /></Link>
              </div>
            )}

            <form onSubmit={submit} className="space-y-6" data-testid="checkout-form">
              {/* About you */}
              <section className="bnp-card p-6 md:p-7" data-testid="checkout-section-personal">
                <p className="bnp-chip bnp-chip-light">About you</p>
                <div className="mt-4 grid sm:grid-cols-12 gap-4">
                  <div className="sm:col-span-3">
                    <label className={labelCls}>Title</label>
                    <select value={form.title} onChange={(e) => set("title", e.target.value)} className="bnp-input" data-testid="checkout-title">
                      {TITLES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="sm:col-span-4">
                    <label className={labelCls}>First name *</label>
                    <input required value={form.first_name} onChange={(e) => set("first_name", e.target.value)} className="bnp-input" data-testid="checkout-first-name" />
                  </div>
                  <div className="sm:col-span-5">
                    <label className={labelCls}>Last name *</label>
                    <input required value={form.last_name} onChange={(e) => set("last_name", e.target.value)} className="bnp-input" data-testid="checkout-last-name" />
                  </div>
                  <div className="sm:col-span-7">
                    <label className={labelCls}>Email *</label>
                    <input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="bnp-input" data-testid="checkout-email" />
                  </div>
                  <div className="sm:col-span-5">
                    <label className={labelCls}>Phone *</label>
                    <input required type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="bnp-input" placeholder="07700 900000" data-testid="checkout-phone" />
                  </div>
                </div>
              </section>

              {/* Address */}
              <section className="bnp-card p-6 md:p-7" data-testid="checkout-section-address">
                <p className="bnp-chip bnp-chip-light">Your address</p>
                <div className="mt-4 grid sm:grid-cols-12 gap-4">
                  <div className="sm:col-span-3">
                    <label className={labelCls}>House no. *</label>
                    <input required value={form.house_number} onChange={(e) => set("house_number", e.target.value)} className="bnp-input" data-testid="checkout-house-number" />
                  </div>
                  <div className="sm:col-span-9">
                    <label className={labelCls}>Street *</label>
                    <input required value={form.street} onChange={(e) => set("street", e.target.value)} className="bnp-input" data-testid="checkout-street" />
                  </div>
                  <div className="sm:col-span-5">
                    <label className={labelCls}>City *</label>
                    <input required value={form.city} onChange={(e) => set("city", e.target.value)} className="bnp-input" data-testid="checkout-city" />
                  </div>
                  <div className="sm:col-span-4">
                    <label className={labelCls}>County</label>
                    <input value={form.county} onChange={(e) => set("county", e.target.value)} className="bnp-input" data-testid="checkout-county" />
                  </div>
                  <div className="sm:col-span-3">
                    <label className={labelCls}>Postcode *</label>
                    <input required value={form.postcode} onChange={(e) => set("postcode", e.target.value)} className="bnp-input" placeholder="SW1A 2AA" data-testid="checkout-postcode" />
                  </div>
                </div>
              </section>

              {/* About the business */}
              <section className="bnp-card p-6 md:p-7" data-testid="checkout-section-business">
                <p className="bnp-chip bnp-chip-light">About your business</p>
                <div className="mt-4 grid sm:grid-cols-12 gap-4">
                  <div className="sm:col-span-12">
                    <label className={labelCls}>Business name we&rsquo;re protecting *</label>
                    <input
                      required
                      value={form.business_name}
                      onChange={(e) => set("business_name", e.target.value)}
                      className="bnp-input bg-[#f7f7f7]"
                      readOnly={Boolean(params.get("business_name"))}
                      data-testid="checkout-business-name"
                    />
                    {params.get("business_name") && (
                      <p className="text-[11px] text-[#447694] mt-1">Pulled from your search. <Link to="/check" className="underline">Run a new check</Link> to change.</p>
                    )}
                  </div>
                  <div className="sm:col-span-7">
                    <label className={labelCls}>Nature of business / sector *</label>
                    <input
                      required
                      type="text"
                      value={form.nature_of_business}
                      onChange={(e) => set("nature_of_business", e.target.value)}
                      placeholder="e.g. hairdressing, plumbing, accountancy"
                      className="bnp-input"
                      data-testid="checkout-nature"
                    />
                  </div>
                  <div className="sm:col-span-5">
                    <label className={labelCls}>When did the business launch? *</label>
                    <select required value={form.business_launch} onChange={(e) => set("business_launch", e.target.value)} className="bnp-input" data-testid="checkout-launch-year">
                      <option value="">Select year</option>
                      {LAUNCH_YEARS.map((y) => <option key={y} value={String(y)}>{y}</option>)}
                      <option value="pre_1996">Before 1996</option>
                      <option value="not_yet">Not launched yet</option>
                    </select>
                  </div>
                  <div className="sm:col-span-12">
                    <label className={labelCls}>How did you hear about us? *</label>
                    <select required value={form.referral_source} onChange={(e) => set("referral_source", e.target.value)} className="bnp-input" data-testid="checkout-referral">
                      <option value="">Please select</option>
                      {REFERRAL_SOURCES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                  </div>
                  <div className="sm:col-span-12">
                    <label className={labelCls}>Anything else you&rsquo;d like us to know? (optional)</label>
                    <textarea rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} className="bnp-input" data-testid="checkout-notes" />
                  </div>
                </div>
              </section>

              {/* Referral code - optional, awards a free first month on monthly only.
                  Hidden entirely on annual; we surface a small notice instead. */}
              {planId === "monthly" ? (
                <section className="bnp-card p-6 md:p-7" data-testid="checkout-referral-section">
                  <div className="flex items-center gap-2.5">
                    <Gift size={18} className="text-[#0d9b80]" />
                    <h3 className="font-display text-lg text-[#04273d] font-semibold">Referral code (optional)</h3>
                  </div>
                  <p className="text-xs text-[#447694] mt-1.5">
                    Was a customer&rsquo;s referral code passed to you? Apply it here and your <strong className="text-[#04273d]">first month is free</strong>. Referrals are only redeemable on the monthly plan.
                  </p>
                  <div className="mt-4 flex items-stretch gap-2">
                    <input
                      type="text"
                      value={referralCode}
                      onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                      placeholder="e.g. SOPHIE-30DE"
                      className="bnp-input flex-1 font-mono-brand tracking-wider uppercase placeholder:normal-case placeholder:font-display placeholder:tracking-normal"
                      data-testid="checkout-referral-code-input"
                      autoComplete="off"
                    />
                    <div className="flex items-center justify-center px-4 min-w-[110px]" data-testid="checkout-referral-status">
                      {referralStatus === "checking" && (
                        <Loader2 size={16} className="animate-spin text-[#447694]" />
                      )}
                      {referralStatus === "valid" && (
                        <div className="flex items-center gap-1.5 text-[#0d9b80] text-sm font-semibold" data-testid="checkout-referral-valid">
                          <CheckCircle2 size={16} /> Valid
                        </div>
                      )}
                      {referralStatus === "invalid" && (
                        <div className="flex items-center gap-1.5 text-red-600 text-sm font-semibold" data-testid="checkout-referral-invalid">
                          <XCircle size={16} /> Not found
                        </div>
                      )}
                    </div>
                  </div>
                  {referralStatus === "valid" && (
                    <p className="text-xs text-[#0d9b80] mt-2.5 font-semibold" data-testid="checkout-referral-reward">
                      {referralReward}
                    </p>
                  )}
                </section>
              ) : (
                referralCode && (
                  <section className="rounded-2xl border-2 border-amber-300 bg-amber-50 p-5 flex items-start gap-3" data-testid="checkout-referral-annual-notice">
                    <Gift size={18} className="text-amber-600 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-display font-semibold text-[#04273d] text-sm">
                        Referral code <span className="font-mono-brand tracking-wider">{referralCode}</span> can&rsquo;t be applied to an annual plan
                      </p>
                      <p className="text-xs text-[#447694] mt-1.5 leading-relaxed">
                        Referrals give you your <strong className="text-[#04273d]">first month free</strong> and only apply to monthly protection. Switch to monthly to redeem this code.
                      </p>
                      <button
                        type="button"
                        onClick={() => setPlanId("monthly")}
                        className="mt-3 inline-flex items-center gap-1.5 text-xs font-display font-semibold text-[#04273d] bg-white hover:bg-[#f7f7f7] border-2 border-[#04273d] px-3 py-1.5 rounded-full transition-colors"
                        data-testid="checkout-referral-switch-to-monthly"
                      >
                        Switch to monthly to redeem
                      </button>
                    </div>
                  </section>
                )
              )}

              {/* Compulsory T&C consent - sits directly above the pay button
                  so it can't be skimmed past. Uses a red border/text when the
                  user tries to submit without ticking so the requirement is
                  unmistakable. Link opens the same PDF the footer serves. */}
              <div
                className={`rounded-xl border p-4 transition-colors ${
                  termsError
                    ? "border-red-400 bg-red-50"
                    : "border-[#9ac7f7]/40 bg-[#f7f7f7]"
                }`}
                data-testid="checkout-terms-wrapper"
              >
                <label
                  htmlFor="checkout-terms-checkbox"
                  className="flex items-start gap-3 cursor-pointer"
                >
                  <input
                    id="checkout-terms-checkbox"
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={(e) => {
                      setTermsAccepted(e.target.checked);
                      if (e.target.checked) setTermsError(false);
                    }}
                    className="mt-1 h-4 w-4 accent-[#04273d] shrink-0"
                    data-testid="checkout-terms-checkbox"
                    aria-invalid={termsError}
                    aria-describedby={termsError ? "checkout-terms-error" : undefined}
                  />
                  <span className="text-sm text-[#04273d] leading-relaxed">
                    I have read and agree to the{" "}
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/static/legal/terms-and-conditions.pdf`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline font-medium hover:text-[#447694]"
                      data-testid="checkout-terms-link"
                    >
                      Terms and Conditions
                    </a>
                    .
                  </span>
                </label>
                {termsError && (
                  <p
                    id="checkout-terms-error"
                    className="mt-2 text-xs text-red-600 pl-7"
                    data-testid="checkout-terms-error"
                  >
                    You must agree to the Terms and Conditions before continuing.
                  </p>
                )}
              </div>

              <button type="submit" disabled={busy || !hasSearched} className="btn-primary w-full justify-center text-base py-4" data-testid="checkout-submit">
                {busy ? <Loader2 className="animate-spin" size={16} /> : <Lock size={14} />}
                {busy ? "Redirecting to Stripe…" : `Continue to secure payment (${referralActive ? "first month free" : plan.today})`}
              </button>
              <p className="text-[11px] text-[#447694] text-center">
                You&rsquo;ll be redirected to Stripe to complete payment securely. Your card details never touch our servers.
              </p>
            </form>
          </div>

          {/* Order summary */}
          <aside className="lg:col-span-1">
            <div className="lg:sticky lg:top-24 space-y-5">
              <div className="bnp-card p-6" data-testid="checkout-summary-card">
                <p className="bnp-chip bnp-chip-light">Order summary</p>
                <h3 className="font-display text-xl text-[#04273d] mt-3 font-semibold">{PLAN_LABEL[planId]}</h3>
                <p className="font-display text-3xl text-[#04273d] mt-2 font-bold">
                  {plan.hero}<span className="text-sm text-[#447694] font-normal">{plan.suffix}</span>
                </p>
                <p className="text-xs text-[#447694] mt-1">{plan.subtitle}</p>

                <div className="mt-5 pt-5 border-t border-[#9ac7f7]/25 space-y-2 text-sm">
                  {plan.breakdown.map((row, i) => (
                    <div key={i} className={`flex justify-between ${row.bold ? "font-semibold pt-2 border-t border-[#9ac7f7]/25" : ""}`}>
                      <span className={row.bold ? "text-[#04273d]" : "text-[#447694]"}>{row.label}</span>
                      <span className="text-[#04273d]">{row.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bnp-card p-5 bg-[#71edc9]/15 border-[#71edc9]/40">
                <Scale size={18} className="text-[#04273d]" />
                <p className="font-display font-semibold text-sm text-[#04273d] mt-2">Up to £10,000 passing-off legal assistance included</p>
                <p className="text-xs text-[#447694] mt-1.5 leading-relaxed">
                  Real legal backing if a competitor ever copies your name. Same with both plans.
                </p>
              </div>

              <div className="bnp-card p-5">
                <Shield size={18} className="text-[#71edc9]" />
                <p className="font-display font-semibold text-sm text-[#04273d] mt-2">Trusted by 2000+ UK businesses.</p>
                <p className="text-xs text-[#447694] mt-1.5 leading-relaxed">
                  Powered by NBR Group.
                </p>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
}
